import React, { useEffect, useState } from 'react'

import clsx from 'clsx'

// Components
import DataNotAvailable from '../../../../components/common/alerts/data-not-available/DataNotAvailable'
import CustomReactSelect from '../../../../components/common/select/react-select/CustomReactSelect'
import CompetitorEventRegisterWrapper from '../../CompetitorEventRegisterWrapper'
import { HeaderHorseItems } from './components/HeaderHorseItems'

import { selectedEvent } from '../../../../store/events/eventsSlice'
import { useAppSelector } from '../../../../store/hooks'
import {
  selectRegistration,
  selectRegistrationByDay,
} from '../../../../store/registration/registrationSlice'
import { selectHorses } from '../../../../store/horses/horseSlice'

import { getFloatPrice } from '../../../../helpers/price'
import { getAllRegistrationFees } from '../../../../helpers/registrationFees'
import { getCurrency } from '../../../../helpers/payment'
import { normalizeOrder } from '../../../../helpers/helpers'

import useEventRegister from '../../../../hooks/useEventRegister'

import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'
import { FeesTypeItem } from '../../../../models/event-fees/event-fees.interface'

// Styles
import { registerCustomStyles } from '../../../../components/customStyles/ReactSelectCustomStyle'
import './clinicNOtherReviewAndPayTab.css'

import { HORSE_NAME_DEFAULT } from '../../../../models/registrations-by-day/registrationByDay.interface'

type IClinicNOtherRegisterHorseTabProps = {
  title?: string
  description?: string
}

export interface IHandleHorseChange {
  id: string
  currentRow: IRegistrationByDayInterface
  removedIndex?: number
}

const ClinicNOtherRegisterHorseTab = ({
  title,
  description,
}: IClinicNOtherRegisterHorseTabProps) => {
  const { onRegistrationByDayHorseChange, updateRegistrationByDay } = useEventRegister()

  const selectedEventR = useAppSelector(selectedEvent)
  const registrationsByDay = useAppSelector(selectRegistrationByDay)
  const myHorses = useAppSelector(selectHorses)
  const registration = useAppSelector(selectRegistration)
  const [loading, setLoading] = useState(false)
  const [loadingCheckbox, setLoadingCheckbox] = useState(false)
  const [registrationsByDayEx, setRegistrationsByDayEx] = useState<
    (IRegistrationByDayInterface & { fee: FeesTypeItem | null })[]
  >([])

  const handleHorseChange = async ({ id, currentRow, removedIndex }: IHandleHorseChange) => {
    const horse = myHorses.find((horse) => horse.id === id) || null

    await onRegistrationByDayHorseChange({
      currentRow,
      horse,
      removedIndex,
      noHorse: id === HORSE_NAME_DEFAULT,
    })
  }

  const updateRegistrationByDayKey = async ({
    currRegistrationByDay,
    key,
  }: {
    currRegistrationByDay: IRegistrationByDayInterface
    key: 'isBreak' | 'isQualify'
  }) => {
    if (!registration) return null

    setLoadingCheckbox(true)

    const registrationsByDayToUpdate = [
      {
        ...currRegistrationByDay,
        [key]: !currRegistrationByDay[key],
      },
    ]

    await updateRegistrationByDay({ registrationsByDayToUpdate })
    setLoadingCheckbox(false)
  }

  useEffect(() => {
    const registrationsByDayWithFees = registrationsByDay.map((item) => {
      const fee = getAllRegistrationFees(selectedEventR?.EventFees?.registrationFees || [])?.find(
        (registrationFee) => registrationFee.uuid === item.uuid
      )
      return { ...item, fee: fee || null }
    })

    setRegistrationsByDayEx(registrationsByDayWithFees)
  }, [registrationsByDay, selectedEventR?.EventFees?.registrationFees])

  const currency = getCurrency(selectedEventR?.EventPaymentSettings?.currency)

  return (
    <CompetitorEventRegisterWrapper title={title} description={description}>
      {registrationsByDayEx?.[0] && (
        <div className="hidden md:block">
          <HeaderHorseItems EventFees={selectedEventR?.EventFees} />
        </div>
      )}
      <div className="flex flex-col gap-2">
        {registrationsByDayEx?.[0] ? (
          [...registrationsByDayEx]
            ?.sort((a, b) => normalizeOrder(a.fee?.order) - normalizeOrder(b.fee?.order))
            ?.filter((row) => !row.isDivision)
            .map((item, currRegistrationByDayIndex) => {
              const values: { index: number; label: string; value: string }[] = []
              let count = 0

              if (item.horseId === HORSE_NAME_DEFAULT) {
                values.push({
                  index: 0,
                  value: HORSE_NAME_DEFAULT,
                  label: HORSE_NAME_DEFAULT,
                })
              } else if (item.horsesSelected && item.horsesSelected?.length > 0) {
                item.horsesSelected?.forEach((horseSelected) => {
                  values.push({
                    index: count,
                    value:
                      myHorses?.find((horse) => horse.id === horseSelected.id)?.id ||
                      HORSE_NAME_DEFAULT,
                    label:
                      myHorses?.find((horse) => horse.id === horseSelected.id)?.horseName ||
                      HORSE_NAME_DEFAULT,
                  })
                  count++
                })
              }

              return (
                <div
                  key={currRegistrationByDayIndex}
                  className={clsx(
                    `w-full flex justify-between flex-wrap lg:mb-0 gap-2 text-SeabiscuitDark200ThemeColor`
                  )}
                >
                  <div
                    className={clsx(
                      'w-full md:w-auto md:flex-1 gap-2 text-base flex flex-wrap items-center pr-4 py-3 pl-4 border rounded-lg border-SeabiscuitLightThemeColorD3'
                    )}
                  >
                    {item.registrationByDayName}
                    {item.fee?.order ? (
                      <span className="text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
                        {item.fee.order}
                      </span>
                    ) : null}
                    <div className="ml-auto flex flex-wrap items-center justify-end text-SeabiscuitDark200ThemeColor/50">
                      <span className="text-SeabiscuitDark200ThemeColor mr-1">
                        {item.qualifyFee &&
                        selectedEventR?.EventFees?.qualifyingClasses?.isEnable &&
                        item.qualifyFeeName
                          ? getFloatPrice(`${currency}${item.qualifyFee}`)
                          : ''}
                      </span>{' '}
                      {item.qualifyFeeName || 'No extra fee'} •{' '}
                      <span className="mx-1 text-SeabiscuitDark200ThemeColor mr-1">
                        {item.breakTime || '0'} min
                      </span>{' '}
                      break •{' '}
                      <span className="ml-1 text-SeabiscuitMainThemeColor">{item.riderName}</span>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      'w-full md:w-[20%] min-w-[180px] flex items-center border border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor text-sm rounded-lg relative'
                    )}
                  >
                    <CustomReactSelect
                      isMulti
                      onChange={async (_currenValue, actionMeta: any) => {
                        setLoading(true)
                        await handleHorseChange({
                          id: actionMeta?.option?.value
                            ? actionMeta.option?.value
                            : actionMeta?.removedValue?.value
                              ? actionMeta.removedValue?.value
                              : '',
                          currentRow: item,
                          removedIndex: actionMeta?.removedValue?.index,
                        })
                        setLoading(false)
                      }}
                      options={[
                        { label: HORSE_NAME_DEFAULT, value: HORSE_NAME_DEFAULT },
                        ...myHorses.map((horse, index) => ({
                          index,
                          label: horse.horseName || HORSE_NAME_DEFAULT,
                          value: horse.id || HORSE_NAME_DEFAULT,
                        })),
                      ]}
                      value={values}
                      isClearable={false}
                      isSearchable={false}
                      isLoading={loading}
                      isDisabled={loading}
                      styles={registerCustomStyles}
                      placeholder="Select Horses"
                      className={`w-full rounded-lg searchableComponent focus:ring-0 p-0 focus:ring-transparent h-full flex items-center capitalize ${!myHorses.length ? 'bg-[#f8f8ff]' : ''}`}
                    />
                  </div>
                  <div className="ml-auto flex flex-col">
                    <div className="block md:hidden">
                      <HeaderHorseItems EventFees={selectedEventR?.EventFees} />
                    </div>
                    <div className="flex-1 flex gap-2">
                      {selectedEventR?.EventFees?.rideTimeRequirement?.isEnable && (
                        <div
                          className={clsx(
                            'ml-auto group w-[55px] min-h-[55px] flex items-center justify-center border border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor text-sm rounded-lg relative',
                            Number(item?.breakTime) > 0 && 'cursor-pointer',
                            loadingCheckbox && 'pointer-events-none opacity-50'
                          )}
                          onClick={async () => {
                            if (Number(item?.breakTime) > 0)
                              await updateRegistrationByDayKey({
                                currRegistrationByDay: item,
                                key: 'isBreak',
                              })
                          }}
                        >
                          <div
                            className={clsx(
                              'w-5 h-5 rounded-full select-none',
                              Number(item?.breakTime) > 0
                                ? 'border-SeabiscuitDark200ThemeColor cursor-pointer group-hover:group-hover:opacity-70'
                                : 'border-SeabiscuitDark200ThemeColor/20',
                              !item.isBreak && 'border-2'
                            )}
                          >
                            {item.isBreak && (
                              <img className="w-full" src="/assets/cp_icons/Ok-3.svg" alt="" />
                            )}
                          </div>
                        </div>
                      )}
                      {selectedEventR?.EventFees?.qualifyingClasses?.isEnable && (
                        <div
                          className={clsx(
                            'group w-[55px] min-h-[55px] flex items-center justify-center border border-SeabiscuitLightThemeColorD3 text-SeabiscuitDark200ThemeColor text-sm rounded-lg relative',
                            Number(item?.qualifyFee) > 0 && 'cursor-pointer',
                            loadingCheckbox && 'pointer-events-none opacity-50'
                          )}
                          onClick={async () => {
                            if (Number(item?.qualifyFee) > 0)
                              await updateRegistrationByDayKey({
                                currRegistrationByDay: item,
                                key: 'isQualify',
                              })
                          }}
                        >
                          <div
                            className={clsx(
                              'w-5 h-5 rounded-full select-none',
                              Number(item?.qualifyFee) > 0
                                ? 'border-SeabiscuitDark200ThemeColor cursor-pointer group-hover:opacity-70'
                                : 'border-SeabiscuitDark200ThemeColor/20',
                              !item.isQualify && 'border-2'
                            )}
                          >
                            {item.isQualify && (
                              <img className="w-full" src="/assets/cp_icons/Ok-3.svg" alt="" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
        ) : (
          <DataNotAvailable
            mode="text"
            containerClassName="text-SeabiscuitDark200ThemeColor text-nr 2xl:text-base"
            text="Please add a rider before adding a horse"
          />
        )}
      </div>
    </CompetitorEventRegisterWrapper>
  )
}

export default ClinicNOtherRegisterHorseTab
